import { InputField } from "@/components/input-field/InputField";
import { CustomPopover } from "@/components/popover/CustomPopover";
import { SelectComponent } from "@/components/select/Select";
import DatePicker from "@/components/date-picker";

import {
  AsyncAutocompleteSelect,
  Option,
} from "@/components/select/AsyncAutocompleteSelect";
import { AppColors } from "@/styles/colors";

import {
  Badge,
  Box,
  Button,
  Field,
  Flex,
  Grid,
  GridItem,
  HStack,
  Text,
} from "@chakra-ui/react";
import dayjs from "dayjs";
import { Filter, Search } from "lucide-react";
import { Dispatch, SetStateAction, useRef, useState } from "react";
import { Control, FieldValues, UseFormHandleSubmit } from "react-hook-form";
import { getCuentasByBusqueda } from "@/services/accounts.service";

interface Props {
  control: Control<FieldValues>;
  handleSubmit: UseFormHandleSubmit<FieldValues>;
  openFilters: boolean;
  setOpenFilters: Dispatch<SetStateAction<boolean>>;
  submitFilters: (event: FieldValues) => Promise<void>;
  clearFilters: () => Promise<void>;
  disableFilters?: boolean;
  loadingFilters?: boolean;
  filtersApplied?: boolean;
  disableClear?: boolean;
  disableApply?: boolean;
}

export const TaxesLoadedFilter = ({
  openFilters,
  disableFilters,
  loadingFilters,
  filtersApplied,
  disableClear,
  submitFilters,
  handleSubmit,
  control,
  setOpenFilters,
  disableApply,
  clearFilters,
}: Props) => {
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const [loadingAccounts, setLoadingAccounts] = useState<boolean>(false);

  const getOptions = async (inputValue: string) => {
    const filtered = await getCuentasByBusqueda({
      busqueda: inputValue,
    });

    return (
      filtered?.map((p) => ({
        label: `${p.titular} - ${p.nroCuenta} - ${p.tipoCuenta}`,

        value: p.nroCuenta,
      })) ?? []
    );
  };

  const loadOptions = (inputValue: string): Promise<Option[]> => {
    return new Promise((resolve) => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef?.current);
      }

      timeoutRef.current = setTimeout(() => {
        if (inputValue.trim().length < 3) {
          resolve([]);

          return;
        }

        setLoadingAccounts(true);

        const options = getOptions(inputValue);

        setLoadingAccounts(false);

        resolve(options);
      }, 1000);
    });
  };

  return (
    <Flex width="100%" paddingY="10px" justifyContent="flex-end">
      <HStack gap="10">
        <CustomPopover
          open={openFilters}
          onOpenChange={(e) => setOpenFilters(e.open)}
          triggerComponent={
            <Box position="relative" display="inline-block">
              <Button
                variant="outline"
                paddingX="12px"
                height="32px"
                disabled={disableFilters || loadingFilters}
              >
                <Filter />

                <Text fontWeight={600} fontSize="14px">
                  Filtrar
                </Text>
              </Button>

              {filtersApplied && (
                <Badge
                  position="absolute"
                  top="-4px"
                  right="-4px"
                  bg="red.500"
                  color="white"
                  fontSize="10px"
                  borderRadius="full"
                  px="2"
                  py="0.5"
                >
                  !
                </Badge>
              )}
            </Box>
          }
          bodyComponent={
            <Flex
              as="form"
              flexDir="column"
              padding="25px"
              gap="15px"
              onSubmit={handleSubmit(submitFilters)}
            >
              <Flex alignItems="center" justifyContent="space-between">
                <Text fontWeight={600} fontSize="20px">
                  Filtrar
                </Text>

                <Button
                  variant="plain"
                  color={AppColors.BLUE_TEXT}
                  fontWeight={600}
                  onClick={clearFilters}
                  disabled={loadingFilters || disableClear}
                >
                  Limpiar filtros
                </Button>
              </Flex>

              <Grid templateColumns="repeat(2, 1fr)" gap={4}>
                <GridItem>
                  <DatePicker
                    control={control}
                    labelText="Fecha"
                    name="fecha"
                    placeholder="Ingresar fecha"
                    maxDate={dayjs()}
                    disabled={disableFilters || loadingFilters}
                  />
                </GridItem>

                <GridItem>
                  <Field.Root>
                    <Field.Label>
                      <Text fontWeight={600}>Cuenta</Text>
                    </Field.Label>

                    <AsyncAutocompleteSelect
                      control={control}
                      loadOptions={loadOptions}
                      name="titular"
                      placeholder="Buscar..."
                      isLoading={loadingAccounts}
                      disabled={
                        disableFilters || loadingFilters || loadingAccounts
                      }
                      closeMenuOnSelect
                      isClearable
                      triggerWithLength
                    />
                  </Field.Root>
                </GridItem>

                <GridItem>
                  <InputField
                    inputName="tipoImpuesto"
                    labelText="Tipo de impuesto"
                    placeholder="Ingresar tipo"
                    control={control}
                    disabled={disableFilters || loadingFilters}
                    startElement={<Search />}
                  />
                </GridItem>

                <GridItem>
                  <SelectComponent
                    name="estado"
                    items={[
                      { label: "Pendiente", value: "Pendiente" },

                      { label: "Cobrado", value: "Cobrado" },

                      { label: "Error", value: "Error" },
                    ]}
                    labelText="Estado"
                    placeholder="Seleccionar"
                    control={control}
                    disabled={disableFilters || loadingFilters}
                    menuMaxHeight="250px"
                    minHeight="42px"
                    clearable
                  />
                </GridItem>
              </Grid>

              <Button
                bgColor={AppColors.GREEN_PRIMARY}
                type="submit"
                loading={loadingFilters}
                disabled={
                  loadingFilters || disableFilters || Boolean(disableApply)
                }
              >
                Aplicar filtros
              </Button>
            </Flex>
          }
        />
      </HStack>
    </Flex>
  );
};
